// Clinic membership is recorded in two places, and they mean different things.
//
//   player.groupId   — the member's PRIMARY clinic. One value. This is what the
//                      roster, the issues list, placement counts and the tier
//                      label all read, so it stays a single id.
//   group.playerIds  — everyone who attends that group. This is the full truth.
//
// A member enrolled in two clinics is primary in one and secondary in the
// other: the second enrolment lives only in that group's playerIds. Weekend
// open-play and private sessions already worked this way (see
// utils/householdGroups.js) — this file generalises it to weekday clinics so a
// member can be added to a second clinic without losing the first.

// Weekend open play / privates. They're bookings, not clinic placements, so
// being in several of them was never a conflict worth warning about.
export function isSession(group) {
  return Boolean(group?.kind);
}

// Every weekday instruction group this member attends, primary or secondary.
export function clinicsForPlayer(playerId, groups) {
  return (groups || []).filter(g => !isSession(g) && (g.playerIds || []).includes(playerId));
}

// The clinics they're in *other than* the one being considered — the set that
// makes an assignment a second-clinic decision rather than a plain placement.
export function otherClinicsForPlayer(playerId, groups, excludeGroupId) {
  return clinicsForPlayer(playerId, groups).filter(g => g.id !== excludeGroupId);
}

// { playerId: number of clinics } in one pass, for badging a whole list
// without re-scanning every group per card.
export function clinicCounts(groups) {
  const counts = {};
  (groups || []).forEach(g => {
    if (isSession(g)) return;
    (g.playerIds || []).forEach(id => { counts[id] = (counts[id] || 0) + 1; });
  });
  return counts;
}

// Which clinic should be primary once `groups` has changed. Keeps the current
// one if they're still in it; otherwise promotes another clinic they attend,
// so removing someone from one of two clinics leaves them placed in the other
// rather than reading as unassigned.
export function resolvePrimaryGroupId(player, groups) {
  if (!player) return null;
  const stillIn = (groups || []).some(
    g => g.id === player.groupId && (g.playerIds || []).includes(player.id)
  );
  if (player.groupId && stillIn) return player.groupId;
  const next = clinicsForPlayer(player.id, groups)[0];
  return next ? next.id : null;
}
